import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, Button, Card, ListCell, Skeleton, Switch } from "@/components/ui";
import { errorMessage } from "@/lib/api/error";
import { useMe, useUpdateMySettings } from "@/lib/api/queries";
import type { TabKey } from "@/lib/api/types";
import { useLogout } from "@/lib/logout";
import { TABS } from "@/lib/tabs";

export default function SettingsPage() {
  const { data: me, isLoading, isError } = useMe();
  const save = useUpdateMySettings();
  const logout = useLogout();

  // 저장 중에도 방금 누른 상태가 보여야 한다 — 서버 응답을 기다리는 동안 스위치가
  // 옛 값으로 돌아가면 두 번 누르게 된다.
  const hidden = new Set<TabKey>(save.variables ?? me?.hidden_tabs ?? []);

  function handleToggle(key: TabKey, visible: boolean) {
    // 감출 것만 보낸다. 노출이 기본이라 다음에 기능이 생기면 저절로 보인다.
    const next = new Set(hidden);
    if (visible) next.delete(key);
    else next.add(key);
    // 순서는 `TABS` 순서로 고정한다 — Set 순서(누른 순서)를 그대로 보내면 저장된
    // 목록이 화면과 무관하게 뒤섞인다.
    save.mutate(
      TABS.filter((tab) => next.has(tab.key)).map((tab) => tab.key),
      {
        onError: (error) =>
          toast.error(errorMessage(error, "설정을 저장하지 못했어요. 다시 시도해 주세요.")),
      }
    );
  }

  return (
    <AppShell title="설정">
      <section>
        <h2 className="text-label-1 font-semibold text-label-normal">탭 노출</h2>
        <p className="mt-1 text-body-2 text-label-alternative">
          안 쓰는 기능은 탭에서 감출 수 있어요. 감춰도 주소를 알면 그대로 들어갈 수 있어요.
        </p>

        {isError && (
          <div className="mt-4">
            {/* 못 읽으면 탭은 전부 보이는 상태로 돈다(`AppShell`). 저장은 눌러 볼 수
                있게 열어 둔다 — 다시 시도하는 게 유일한 다음 행동이다. */}
            <Alert tone="cautionary">설정을 읽지 못했어요. 지금은 모든 탭이 보여요.</Alert>
          </div>
        )}

        {isLoading ? (
          <Skeleton rows={TABS.length} className="mt-4" />
        ) : (
          <Card padding={8} className="mt-4">
            {TABS.map((tab) => (
              <ListCell
                key={tab.key}
                title={tab.label}
                description={hidden.has(tab.key) ? "탭에서 감춤" : "탭에 보임"}
                trailing={
                  <Switch
                    checked={!hidden.has(tab.key)}
                    disabled={save.isPending}
                    onChange={(e) => handleToggle(tab.key, e.target.checked)}
                    aria-label={`${tab.label} 탭 보이기`}
                  />
                }
              />
            ))}
          </Card>
        )}

        <p className="mt-3 text-caption-1 text-label-alternative">
          전부 감추면 탭 줄이 비지만, 이 설정 화면 입구는 늘 남아 있어요.
        </p>
      </section>

      {/* 로그아웃이 있던 자리는 모든 화면의 헤더였다. 좁은 줄에서 설정 바로 옆에
          있어서 잘못 누르기 쉬웠고, 자주 쓰는 버튼도 아니다 — 계정 이야기가
          모이는 여기로 옮겼다. */}
      <section className="mt-10">
        <h2 className="text-label-1 font-semibold text-label-normal">계정</h2>
        <p className="mt-1 text-body-2 text-label-alternative">
          로그아웃하면 이 브라우저에서 워크스페이스 선택도 함께 지워요.
        </p>
        <div className="mt-3">
          <Button variant="outlined" color="assistive" onClick={logout}>
            로그아웃
          </Button>
        </div>
      </section>
    </AppShell>
  );
}
